import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getShareSurveySupplierUserData,
  getShareSurveySupplierUserHasAccess,
} from "actions/supplierAssessment/supplierAssessmentAction";
import { fallback } from "components/screen/phase-3/riskAdmin/RiskTabs";
import DynamicDrawer from "components/UI/Drawer/DynamicDrawer";
import SearchTextField from "components/UI/textfield/searchTextField";
import { NoDataComponent } from "components/widgets/phase-3/esgMaturity/shareSurvey/NoData";
import { usePaginationSearch } from "customHooks/usePaginationSearch";
import noData from "images/no_data_images/survey_section.svg";
import { useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useSelector } from "react-redux";

const ShareSurveyLinkDrawer = ({
  isDrawerOpen,
  handleCloseDrawer,
  supplierId,
  customLink,
}) => {
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("View All");
  const [accessMap, setAccessMap] = useState({});
  const prevResultRef = useRef([]);

  const { keyword, handleSearch } = usePaginationSearch();
  const queryClient = useQueryClient();

  const { item: { _id, survey_select_id } = {} } = useSelector(
    (s) => s.riskAdminReducer.listings["row"] || {}
  );

  const { data: { result = [], survey_link = "" } = {}, isLoading } = useQuery({
    queryKey: ["survey-link", _id, supplierId],
    queryFn: () =>
      getShareSurveySupplierUserData({
        supplier_id: supplierId,
        survey_id: survey_select_id,
        supplier_assessment_id: _id,
      }),
    enabled: isDrawerOpen && !!supplierId && !customLink,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  useEffect(() => {
    if (!result?.length) return;

    const prev = prevResultRef.current;
    const isSame =
      prev.length === result.length &&
      prev.every(
        (p, i) =>
          p._id === result[i]._id && p.has_access === result[i].has_access
      );

    if (!isSame) {
      setAccessMap(
        Object.fromEntries(result.map((u) => [u._id, u.has_access]))
      );
      prevResultRef.current = result;
    }
  }, [result]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(customLink || survey_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error("Copy failed");
    }
  };

  const filteredUsers = result.filter((u) => {
    const match = [u.name, u.email].some((v) =>
      v?.toLowerCase().includes(keyword.toLowerCase())
    );
    const hasAccess = accessMap[u._id] ?? u.has_access;
    return filter === "Access"
      ? match && hasAccess
      : filter === "No Access"
        ? match && !hasAccess
        : match;
  });

  const handleSave = async () => {
    const user_ids = result.filter((u) => accessMap[u._id]).map((u) => u._id);

    await getShareSurveySupplierUserHasAccess({
      supplier_id: supplierId,
      survey_id: survey_select_id,
      supplier_assessment_id: _id,
      user_ids,
    });

    queryClient.setQueryData(["survey-link", supplierId], (old) => {
      if (!old?.result) return old;
      const updated = old.result.map((u) => ({
        ...u,
        has_access: user_ids.includes(u._id),
      }));
      return { ...old, result: updated };
    });

    queryClient
      .getQueryCache()
      .findAll(["AssessmentList"])
      .forEach(({ queryKey }) =>
        queryClient.setQueryData(queryKey, (old) => {
          if (!old?.result) return old;
          const newResult = old.result.map((item) =>
            item._id === _id
              ? { ...item, status: user_ids.length > 0 ? "active" : "Inactive" }
              : item
          );
          return { ...old, result: newResult };
        })
      );

    handleCloseDrawer();
  };

  const getInitials = (name) =>
    name
      ?.trim()
      ?.split(" ")
      ?.map((n, i, arr) => (arr.length === 1 ? n.slice(0, 2) : n[0]))
      ?.join("")
      ?.slice(0, 2)
      ?.toUpperCase() || "";

  return (
    <DynamicDrawer
      hasDrawerOpen={isDrawerOpen}
      handleClose={handleCloseDrawer}
      heading={
        <Typography variant="h3" fontWeight={600} color="#0F0F0F">
          Share Survey
        </Typography>
      }
      buttonText="Save Changes"
      submitClick={handleSave}
      sx={{ width: "47vw", p: 0 }}
    >
      <Box sx={{ p: 1, mb: 2 }}>
        <Typography variant="h4" fontWeight={500} color="#9A9A9A" mb={1}>
          Share Link
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            fullWidth
            value={customLink || survey_link}
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-input": { color: "#9A9A9A", fontWeight: 400 },
            }}
          />
          <Button
            variant="text"
            size="small"
            onClick={handleCopy}
            sx={{
              gap: 1,
              fontWeight: 500,
              color: "#3374B9",
              minWidth: 170,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            {copied ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: "#4CAF50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 6px rgba(76,175,80,0.5)",
                }}
              >
                <FaCheck size={12} color="#fff" />
              </Box>
            ) : (
              "Copy link to survey"
            )}
          </Button>
        </Box>
        <Typography variant="body2" color="#3374B9" mt={1}>
          This link can only be accessed by people with share access
        </Typography>
      </Box>

      <Box
        sx={{ display: "flex", justifyContent: "space-between", p: 1, mt: -1 }}
      >
        <SearchTextField
          size="small"
          searchClick={handleSearch}
          label="Search by Name, Email ID"
          sx={{ minWidth: "25em" }}
        />
        <Select
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ width: 178 }}
          MenuProps={{ PaperProps: { sx: { width: 178 } } }}
        >
          {["View All", "Access", "No Access"].map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", p: 1 }}>
        {["People with access", "Access Control"].map((t) => (
          <Typography
            key={t}
            variant="h4"
            fontWeight={500}
            color="#9A9A9A"
            p={1}
          >
            {t}
          </Typography>
        ))}
      </Box>

      {isLoading ? (
        fallback({ showFooter: false })
      ) : filteredUsers.length === 0 ? (
        <NoDataComponent imgSrc={noData} />
      ) : (
        <List sx={{ maxHeight: 430, overflowY: "auto", mt: -2 }}>
          {filteredUsers.map((u) => (
            <ListItem
              key={u._id}
              secondaryAction={
                <Select
                  size="small"
                  value={
                    (accessMap[u._id] ?? u.has_access) ? "Access" : "No Access"
                  }
                  onChange={(e) =>
                    setAccessMap((p) => ({
                      ...p,
                      [u._id]: e.target.value === "Access",
                    }))
                  }
                  sx={{ minWidth: 200 }}
                  MenuProps={{ PaperProps: { sx: { minWidth: 200 } } }}
                >
                  {["Access", "No Access"].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              }
            >
              <Avatar
                src={u.image || ""}
                sx={{
                  bgcolor: "#EBF1F8",
                  mr: 2,
                  color: "#3374B9",
                  fontWeight: 600,
                  borderRadius: "8px",
                  width: 40,
                  height: 40,
                  fontSize: "0.9rem",
                }}
              >
                {!u.image && getInitials(u.name)}
              </Avatar>
              <ListItemText
                primary={
                  <Typography variant="h4" fontWeight={600}>
                    {u.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="#505050">
                    {u.email}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </DynamicDrawer>
  );
};

export default ShareSurveyLinkDrawer;
